/**
 * CalendarReservationSearch — Iter 78.
 *
 * Fast-search box for the Bookings Calendar (admin + vendor). Matches
 * partial, case-insensitive Reservation Numbers (booking id) across every
 * booking in the calendar payload, shows a dropdown of matches, and hands
 * the picked booking to the parent so it can jump + highlight the bar.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { formatRangeDay, STATUS_STYLE } from "@/components/calendar/calendarHelpers";

export default function CalendarReservationSearch({ bookings = [], cars = [], onSelect, onClear }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  const carById = useMemo(
    () => Object.fromEntries(cars.map((c) => [c.id, c])),
    [cars],
  );

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^#/, "");
    if (!s) return [];
    const norm = (v) => String(v || "").toLowerCase();
    const compact = (v) => norm(v).replace(/[\s-]/g, "");
    const digits = (v) => String(v || "").replace(/\D/g, "");
    const qCompact = compact(s);
    const qDigits = digits(s);
    return bookings
      .filter((b) => {
        if (norm(b.id).includes(s)) return true;
        if (norm(b.customer_name).includes(s)) return true;
        if (norm(b.customer_email).includes(s)) return true;
        if (qDigits.length >= 3 && digits(b.customer_phone).includes(qDigits)) return true;
        const plate = (carById[b.car_id] || {}).plate;
        if (plate && compact(plate).includes(qCompact)) return true;
        return false;
      })
      .slice(0, 8);
  }, [q, bookings, carById]);

  useEffect(() => {
    const h = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const clear = () => {
    setQ("");
    setOpen(false);
    onClear?.();
  };

  return (
    <div ref={boxRef} className="relative w-full sm:max-w-sm" data-testid="cal-reservation-search">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search reservation #, name, phone, email, plate..."
        className="w-full h-9 pl-9 pr-9 rounded-full border border-border bg-white text-sm outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 transition"
        data-testid="cal-reservation-search-input"
      />
      {q && (
        <button
          type="button"
          onClick={clear}
          aria-label="Clear search"
          className="absolute right-2.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-500 flex items-center justify-center transition"
          data-testid="cal-reservation-search-clear"
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {open && q.trim() && (
        <div
          className="absolute left-0 right-0 top-full mt-1.5 rounded-xl border border-border bg-white shadow-lg z-40 overflow-hidden"
          data-testid="cal-reservation-search-dropdown"
        >
          {matches.length === 0 ? (
            <div className="px-4 py-3 text-sm text-neutral-500" data-testid="cal-reservation-search-empty">
              No matching reservation found.
            </div>
          ) : (
            matches.map((b) => {
              const car = carById[b.car_id] || {};
              const style = STATUS_STYLE[b.status] || STATUS_STYLE.confirmed;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => { onSelect?.(b); setOpen(false); }}
                  className="w-full text-left px-4 py-2.5 hover:bg-blue-50/60 transition border-b border-border/60 last:border-b-0"
                  data-testid={`cal-reservation-result-${b.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-bold text-blue-700">
                      #{String(b.id).slice(0, 8).toUpperCase()}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${style.bg} ${style.text}`}>
                      {style.label}
                    </span>
                  </div>
                  <div className="text-sm font-medium text-neutral-900 truncate mt-0.5">
                    {b.customer_name} · {car.make} {car.model}{car.plate ? ` · ${car.plate}` : ""}
                  </div>
                  <div className="text-xs text-neutral-500">
                    {formatRangeDay(b.start)} → {formatRangeDay(b.end)}
                  </div>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
